import {
  DashboardPageFormTab,
  DashboardPageFormTabList,
  DashboardPageFormTabPanel,
  DashboardPageFormTabs,
  buildDashboardDataTableColumns,
  DashboardDataTableColumnKind,
} from '@shigo-ui/dashboard-ui';

import { dashboardT } from '../../../../../lib/messages';
import { formatCurrency, formatNumber } from '../../../../../lib/format';
import {
  DashboardTruncatedName,
  dashboardTruncatedNameClassName,
} from '../../../../dashboard/components/DashboardTableWidget/DashboardTruncatedName';
import type { dashboardCanvasRendererProps } from '@/components/dashboard/DashboardCanvas';
import {
  networkPerformanceId,
  networkPerformanceName,
  networkPlacementRevenuePercentage,
  type NetworkAppPerformance,
  type NetworkDomainPerformance,
  type NetworkPlacementPerformance,
} from '../model';
import type { networkDashboardRuntime } from '../widgetRuntime';
import { NetworkDashboardWidgetType, type NetworkDashboardWidget } from '../widgetModel';
import { NetworkDashboardTable } from '../NetworkDashboardTable';

type performanceItem =
  NetworkPlacementPerformance | NetworkAppPerformance | NetworkDomainPerformance;

export function NetworkPerformanceTablesRenderer({
  widget,
  runtime,
}: dashboardCanvasRendererProps<NetworkDashboardWidget, networkDashboardRuntime>) {
  if (widget.type !== NetworkDashboardWidgetType.PerformanceTables) return null;
  const loading = runtime.overview.isLoading;
  const errorMessage = runtime.overview.isError
    ? dashboardT('networkDashboard.loadError')
    : undefined;
  return (
    <section className="border-border bg-card min-w-0 overflow-hidden rounded-md border shadow-[var(--shadow-xs)]">
      <DashboardPageFormTabs layout="horizontal" defaultValue="placements">
        <DashboardPageFormTabList align="start">
          <DashboardPageFormTab value="placements">
            {dashboardT('networkDashboard.placements')}
          </DashboardPageFormTab>
          <DashboardPageFormTab value="apps">
            {dashboardT('networkDashboard.apps')}
          </DashboardPageFormTab>
          <DashboardPageFormTab value="domains">
            {dashboardT('networkDashboard.domains')}
          </DashboardPageFormTab>
        </DashboardPageFormTabList>
        <DashboardPageFormTabPanel value="placements">
          <NetworkDashboardTable
            title={dashboardT('networkDashboard.placements')}
            items={runtime.overview.data?.placements ?? []}
            columns={placementColumns()}
            getRowKey={networkPerformanceId}
            sortValue={performanceSortValue}
            pageSize={widget.pageSize}
            loading={loading}
            errorMessage={errorMessage}
            minWidthClassName="min-w-[960px]"
          />
        </DashboardPageFormTabPanel>
        <DashboardPageFormTabPanel value="apps">
          <NetworkDashboardTable
            title={dashboardT('networkDashboard.apps')}
            items={runtime.overview.data?.apps ?? []}
            columns={commonColumns()}
            getRowKey={networkPerformanceId}
            sortValue={performanceSortValue}
            pageSize={widget.pageSize}
            loading={loading}
            errorMessage={errorMessage}
            minWidthClassName="min-w-[960px]"
          />
        </DashboardPageFormTabPanel>
        <DashboardPageFormTabPanel value="domains">
          <NetworkDashboardTable
            title={dashboardT('networkDashboard.domains')}
            items={runtime.overview.data?.domains ?? []}
            columns={commonColumns()}
            getRowKey={networkPerformanceId}
            sortValue={performanceSortValue}
            pageSize={widget.pageSize}
            loading={loading}
            errorMessage={errorMessage}
            minWidthClassName="min-w-[960px]"
          />
        </DashboardPageFormTabPanel>
      </DashboardPageFormTabs>
    </section>
  );
}

function placementColumns() {
  return buildDashboardDataTableColumns<NetworkPlacementPerformance>([
    {
      id: 'name',
      header: dashboardT('networkDashboard.name'),
      kind: DashboardDataTableColumnKind.Custom,
      className: 'w-[260px]',
      sortable: true,
      sortKey: 'name',
      cell: (row) => {
        const name = networkPerformanceName(row);
        return (
          <DashboardTruncatedName name={name}>
            <span className={dashboardTruncatedNameClassName}>{name}</span>
          </DashboardTruncatedName>
        );
      },
    },
    number('adRequests', dashboardT('dashboard.metric.adRequests'), (row) => row.adRequests),
    number(
      'adImpressions',
      dashboardT('dashboard.metric.adImpressions'),
      (row) => row.adImpressions,
    ),
    percent('fillRate', dashboardT('dashboard.metric.fillRate'), (row) => row.adRequestsFilled),
    currency('revenue', dashboardT('dashboard.metric.revenue'), (row) => row.revenue),
    percent(
      'revenuePct',
      dashboardT('dashboard.table.revenuePct'),
      networkPlacementRevenuePercentage,
    ),
    currency('profit', dashboardT('dashboard.metric.profit'), (row) => row.networkProfit),
  ]);
}

function commonColumns<TItem extends performanceItem>() {
  return buildDashboardDataTableColumns<TItem>([
    text(
      'name',
      dashboardT('networkDashboard.name'),
      (row) => networkPerformanceName(row),
      'w-[260px]',
    ),
    number('adRequests', dashboardT('dashboard.metric.adRequests'), (row) => row.adRequests),
    number(
      'adImpressions',
      dashboardT('dashboard.metric.adImpressions'),
      (row) => row.adImpressions,
    ),
    percent('fillRate', dashboardT('dashboard.metric.fillRate'), (row) => row.adRequestsFilled),
    currency('revenue', dashboardT('dashboard.metric.revenue'), (row) => row.revenue),
    currency('profit', dashboardT('dashboard.metric.profit'), (row) => row.networkProfit),
  ]);
}

function text<TItem>(
  id: string,
  header: string,
  value: (row: TItem) => string,
  className?: string,
) {
  return {
    id,
    header,
    kind: DashboardDataTableColumnKind.Text,
    value,
    className,
    sortable: true,
    sortKey: id,
  } as const;
}

function number<TItem>(id: string, header: string, value: (row: TItem) => number | undefined) {
  return {
    id,
    header,
    cell: (row: TItem) => formatNumber(value(row)),
    sortable: true,
    sortKey: id,
  };
}

function currency<TItem>(id: string, header: string, value: (row: TItem) => number | undefined) {
  return {
    id,
    header,
    cell: (row: TItem) => formatCurrency(value(row)),
    sortable: true,
    sortKey: id,
  };
}

function percent<TItem>(id: string, header: string, value: (row: TItem) => number | undefined) {
  return {
    id,
    header,
    cell: (row: TItem) => {
      const current = value(row);
      return current === undefined ? '-' : `${(current * 100).toFixed(2)}%`;
    },
    sortable: true,
    sortKey: id,
  };
}

function performanceSortValue(row: performanceItem, key: string) {
  const values: Record<string, () => string | number | undefined> = {
    name: () => networkPerformanceName(row),
    adRequests: () => row.adRequests,
    adImpressions: () => row.adImpressions,
    fillRate: () => row.adRequestsFilled,
    revenue: () => row.revenue,
    revenuePct: () => networkPlacementRevenuePercentage(row),
    profit: () => row.networkProfit,
  };
  return values[key]?.();
}
